import { useCallback, useState, useRef } from 'react'
import {
  ReactFlow, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, BackgroundVariant,
  useReactFlow, ReactFlowProvider, Panel
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Plus, Trash2, Save, Image, Film, Type, Square } from 'lucide-react'
import useStore from '../store/useStore'
import ImageGeneratorNode from '../components/nodes/ImageGeneratorNode'
import VideoGeneratorNode from '../components/nodes/VideoGeneratorNode'
import AnnotationNode     from '../components/nodes/AnnotationNode'
import GroupNode          from '../components/nodes/GroupNode'
import ContextMenu        from '../components/ui/ContextMenu'

const NODE_TYPES = {
  imageGenerator: ImageGeneratorNode,
  videoGenerator: VideoGeneratorNode,
  annotation:     AnnotationNode,
  group:          GroupNode,
}

const WORKFLOW_TEMPLATES = [
  { id: 'static-ad',   label: '30-Second Ad',      description: '4 clips → assembled ad' },
  { id: 'image-batch', label: 'Static Ad Batch',    description: '20 image variations' },
  { id: 'broll',       label: 'B-Roll Generation',  description: 'Generate B-roll library' },
  { id: 'full-video',  label: '5-Minute Video',     description: 'Full storyboard pipeline' },
]

function CanvasInner() {
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [contextMenu, setContextMenu] = useState(null) // { type, x, y, nodeId?, nodeType? }
  const [showTemplates, setShowTemplates] = useState(false)
  const reactFlowWrapper = useRef(null)
  const { addNotification } = useStore()
  const { screenToFlowPosition, getNode, addNodes, deleteElements } = useReactFlow()

  const onConnect = useCallback(
    (params) => setEdges(eds => addEdge({ ...params, animated: true }, eds)),
    [setEdges]
  )

  // ─── Right-click on empty canvas ────────────────────────────────────────────
  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    setContextMenu({ type: 'pane', x: e.clientX, y: e.clientY })
  }, [])

  // ─── Right-click on a node ──────────────────────────────────────────────────
  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault()
    setContextMenu({ type: 'node', x: e.clientX, y: e.clientY, nodeId: node.id, nodeType: node.type })
  }, [])

  // ─── Add node at position ───────────────────────────────────────────────────
  const handleAddNode = useCallback((type, screenPos) => {
    const position = screenToFlowPosition({ x: screenPos.x, y: screenPos.y })
    const id = `${type}_${Date.now()}`

    const defaults = {
      imageGenerator: { style: { width: 300 } },
      videoGenerator: { style: { width: 300 } },
      annotation:     { style: { width: 220, height: 120 } },
      group:          { style: { width: 320, height: 220 } },
    }

    const newNode = {
      id,
      type,
      position,
      data: { label: type },
      ...(defaults[type] || {}),
    }

    addNodes(newNode)
  }, [screenToFlowPosition, addNodes])

  // ─── Duplicate node ─────────────────────────────────────────────────────────
  const handleDuplicate = useCallback((nodeId) => {
    const node = getNode(nodeId)
    if (!node) return
    const id = `${node.type}_${Date.now()}`
    const newNode = {
      ...node,
      id,
      position: { x: node.position.x + 40, y: node.position.y + 40 },
      selected: false,
    }
    addNodes(newNode)
    addNotification({ type: 'info', message: 'Node duplicated.' })
  }, [getNode, addNodes, addNotification])

  // ─── Delete node ────────────────────────────────────────────────────────────
  const handleDeleteNode = useCallback((nodeId) => {
    deleteElements({ nodes: [{ id: nodeId }] })
    addNotification({ type: 'info', message: 'Node deleted.' })
  }, [deleteElements, addNotification])

  // ─── Clear canvas ───────────────────────────────────────────────────────────
  const clearCanvas = () => {
    setNodes([])
    setEdges([])
    addNotification({ type: 'info', message: 'Canvas cleared.' })
  }

  // ─── Save workflow ──────────────────────────────────────────────────────────
  const saveWorkflow = () => {
    addNotification({ type: 'success', message: 'Workflow saved.' })
  }

  // ─── Toolbar add button ─────────────────────────────────────────────────────
  const addNodeCenter = (type) => {
    const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 }
    handleAddNode(type, center)
  }

  return (
    <div className="flex-1 flex flex-col h-full" ref={reactFlowWrapper}>

      {/* ─── Toolbar ── */}
      <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-surface-card border-b border-surface-border">

        {/* Templates */}
        <div className="relative">
          <button onClick={() => setShowTemplates(!showTemplates)} className="btn-secondary text-xs py-1.5">
            Templates
          </button>
          {showTemplates && (
            <div className="absolute left-0 top-full mt-1 w-56 bg-surface-card border border-surface-border rounded-xl shadow-2xl z-50">
              {WORKFLOW_TEMPLATES.map(t => (
                <button
                  key={t.id}
                  className="w-full text-left px-4 py-2.5 hover:bg-surface-hover first:rounded-t-xl last:rounded-b-xl"
                  onClick={() => { setShowTemplates(false); addNotification({ type: 'info', message: `Template "${t.label}" — coming in Phase 3.` }) }}
                >
                  <div className="text-sm text-slate-200 font-medium">{t.label}</div>
                  <div className="text-xs text-slate-500">{t.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick-add buttons */}
        <div className="flex items-center gap-1.5 border-l border-surface-border pl-3">
          {[
            { type: 'imageGenerator', icon: Image, label: 'Image', color: 'text-blue-400' },
            { type: 'videoGenerator', icon: Film,  label: 'Video', color: 'text-purple-400' },
            { type: 'annotation',     icon: Type,  label: 'Note',  color: 'text-yellow-400' },
            { type: 'group',          icon: Square,label: 'Group', color: 'text-green-400' },
          ].map(({ type, icon: Icon, label, color }) => (
            <button
              key={type}
              onClick={() => addNodeCenter(type)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface border border-surface-border hover:border-brand-500/40 text-xs text-slate-400 hover:text-slate-200 transition-all"
              title={`Add ${label} node`}
            >
              <Icon size={12} className={color} />
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 text-xs text-slate-600 hidden md:block pl-2">
          Right-click canvas to add nodes · Right-click nodes for options
        </div>

        <button onClick={saveWorkflow} className="btn-secondary text-xs py-1.5">
          <Save size={12} /> Save
        </button>
        <button onClick={clearCanvas} className="btn-ghost text-xs py-1.5 text-red-400 hover:text-red-300">
          <Trash2 size={12} /> Clear
        </button>
      </div>

      {/* ─── React Flow Canvas ── */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={NODE_TYPES}
          onPaneContextMenu={onPaneContextMenu}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={() => setContextMenu(null)}
          fitView
          className="bg-surface"
          proOptions={{ hideAttribution: true }}
          deleteKeyCode="Delete"
          selectionKeyCode="Shift"
          multiSelectionKeyCode="Shift"
          zoomOnDoubleClick={false}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#252d42" />
          <Controls position="bottom-right" />
          <MiniMap
            position="bottom-left"
            nodeColor={(node) => {
              if (node.type === 'imageGenerator') return '#3b82f6'
              if (node.type === 'videoGenerator') return '#8b5cf6'
              if (node.type === 'annotation')     return '#f59e0b'
              if (node.type === 'group')          return '#10b981'
              return '#6272f1'
            }}
            maskColor="rgba(15,17,23,0.8)"
          />

          {/* Keyboard shortcut legend */}
          <Panel position="top-right">
            <div className="bg-surface-card border border-surface-border rounded-lg px-3 py-2 text-xs text-slate-500 space-y-0.5">
              <div><kbd className="font-mono text-slate-400">Del</kbd> Delete selected</div>
              <div><kbd className="font-mono text-slate-400">Shift+drag</kbd> Multi-select</div>
              <div><kbd className="font-mono text-slate-400">Scroll</kbd> Zoom</div>
            </div>
          </Panel>
        </ReactFlow>

        {/* Empty state */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-white font-semibold text-lg mb-2">Canvas is empty</h3>
              <p className="text-slate-400 text-sm mb-1">Right-click anywhere to add nodes</p>
              <p className="text-slate-600 text-xs">Or use the toolbar buttons above</p>
            </div>
          </div>
        )}
      </div>

      {/* ─── Context Menu ── */}
      {contextMenu && (
        <ContextMenu
          type={contextMenu.type}
          x={contextMenu.x}
          y={contextMenu.y}
          nodeId={contextMenu.nodeId}
          nodeType={contextMenu.nodeType}
          onClose={() => setContextMenu(null)}
          onAddNode={handleAddNode}
          onDuplicate={handleDuplicate}
          onDelete={handleDeleteNode}
        />
      )}
    </div>
  )
}

// Wrap in ReactFlowProvider so useReactFlow() works inside
export default function Canvas() {
  return (
    <ReactFlowProvider>
      <CanvasInner />
    </ReactFlowProvider>
  )
}
